import React, { useContext, useEffect, useRef, useState } from "react";
import { MaterialReactTable } from "material-react-table";
import { BadgeCheck, Eye } from "lucide-react";
import { base_url } from "../../config";
import axios from "axios";
import EurAIProofModal from "./EurAIProofModal";
import RegistrationContext from "../../contexts/RegistrationContext";
import {
  darkBodyCellSx,
  darkHeadCellSx,
  darkPaperSx,
  darkRowSx,
  darkSearchSx,
  darkToolbarSx,
} from "../Codes/darkTable";

const typeChip = (value) => {
  switch (value) {
    case "student":
      return { label: "Student", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" };
    case "student-paper":
      return { label: "Student paper", cls: "bg-violet-500/15 text-violet-300 border-violet-500/30" };
    case "regular":
      return { label: "Regular", cls: "bg-sky-500/15 text-sky-300 border-sky-500/30" };
    default:
      return { label: value || "—", cls: "bg-slate-500/15 text-slate-300 border-slate-500/30" };
  }
};

const EurAI = () => {
  const { reloadAdminBadges, setReloadAdminBadges } = useContext(RegistrationContext);
  const [data, setData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [pdf, setPDF] = useState(null);
  const [rowData, setRowData] = useState(null);
  const skippedRef = useRef(new Set());

  const fetchEurai = async () => {
    try {
      const response = await axios.get(`${base_url}/api/admin/eurai_pending`);
      return response.data || [];
    } catch (error) {
      console.error("Error fetching pending EurAI claims:", error);
      return [];
    }
  };

  const reloadData = async () => {
    const rows = await fetchEurai();
    setData(rows);
    return rows;
  };

  useEffect(() => {
    reloadData();
  }, []);

  const loadProof = async (row) => {
    try {
      const response = await axios.get(`${base_url}/api/admin/students_pdf`, {
        params: { id: row.eurai_proof_file_id },
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      setPDF(blob);
      setRowData(row);
      setModalOpen(true);
    } catch (error) {
      console.error("Error fetching EurAI proof:", error);
      setModalOpen(false);
    }
  };

  const pickNext = (rows, excludeCode) =>
    rows.find((r) => r.code !== excludeCode && !skippedRef.current.has(r.code));

  const closeModal = () => {
    setModalOpen(false);
    setPDF(null);
    setRowData(null);
  };

  const handleResolved = async () => {
    setReloadAdminBadges(reloadAdminBadges + 1);
    const rows = await reloadData();
    const next = pickNext(rows, rowData?.code);
    if (next) {
      await loadProof(next);
    } else {
      closeModal();
    }
  };

  const handleSkip = async () => {
    if (rowData?.code) skippedRef.current.add(rowData.code);
    const rows = await fetchEurai();
    const next = pickNext(rows, null);
    if (next) {
      await loadProof(next);
    } else {
      closeModal();
    }
  };

  const columns = [
    {
      accessorKey: "code",
      header: "Code",
      size: 100,
      Cell: ({ cell }) => (
        <span className="font-mono text-[11px] px-2 py-0.5 rounded-md bg-slate-700/40 text-slate-100 border border-slate-600/40">
          {cell.getValue()}
        </span>
      ),
    },
    {
      accessorKey: "name",
      header: "Name",
      Cell: ({ cell }) => <span className="text-slate-100">{cell.getValue()}</span>,
    },
    {
      accessorKey: "email",
      header: "Email",
      Cell: ({ cell }) => (
        <span className="text-slate-400 text-xs">{cell.getValue()}</span>
      ),
    },
    {
      accessorKey: "type",
      header: "Type",
      size: 120,
      Cell: ({ cell }) => {
        const { label, cls } = typeChip(cell.getValue());
        return (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium ${cls}`}
          >
            {label}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      enableColumnActions: false,
      size: 110,
      Cell: ({ row }) => (
        <div className="flex justify-end">
          <button
            onClick={() => loadProof(row.original)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-blue-500/20 text-blue-200 border border-blue-500/30 hover:bg-blue-500/30 transition-colors"
          >
            <Eye className="h-3.5 w-3.5" />
            Review
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100">
      <div className="mx-auto max-w-[1500px] px-4 py-5 space-y-4">
        <header className="flex items-center justify-between">
          <h1 className="text-lg font-bold flex items-center gap-2">
            <BadgeCheck className="h-5 w-5 text-emerald-400" />
            EurAI validations
            <span className="text-xs font-normal text-slate-400">
              ({data.length} pending)
            </span>
          </h1>
        </header>

        <div className="rounded-2xl overflow-hidden">
          <MaterialReactTable
            columns={columns}
            data={data}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableHiding={false}
            initialState={{ density: "compact", pagination: { pageSize: 25, pageIndex: 0 } }}
            muiTablePaperProps={{ sx: darkPaperSx }}
            muiTableContainerProps={{ sx: { backgroundColor: "transparent" } }}
            muiTopToolbarProps={{ sx: darkToolbarSx }}
            muiBottomToolbarProps={{ sx: darkToolbarSx }}
            muiTableHeadCellProps={{ sx: darkHeadCellSx }}
            muiTableBodyCellProps={{ sx: darkBodyCellSx }}
            muiTableBodyRowProps={{ sx: darkRowSx }}
            muiSearchTextFieldProps={{ placeholder: "Search…", sx: darkSearchSx }}
          />
        </div>

        <EurAIProofModal
          isOpen={modalOpen}
          onClose={closeModal}
          pdf={pdf}
          row={rowData}
          onAccept={handleResolved}
          onDecline={handleResolved}
          onSkip={handleSkip}
        />
      </div>
    </div>
  );
};

export default EurAI;
